class TradeProjects3DCarousel {
  constructor() {
    this.currentIndex = 0;
    this.totalProjects = 0;
    this.isAnimating = false;
    this.currentLightboxIndex = 0;
    this.lightboxImages = [];
    this.currentProjectTitle = '';
    this.currentProjectSubtitle = '';
    
    // Auto-rotation properties (desktop only)
    this.autoRotateInterval = null;
    this.autoRotateDelay = 3000; // 3 seconds (1-2s active + transition time)
    this.isHovered = false;
    this.isMobile = false;
    
    this.init();
  }

  init() {
    this.carousel = document.getElementById('projectsCarousel');
    this.lightbox = document.getElementById('projectLightbox');
    
    if (!this.carousel || !this.lightbox) return;

    this.projectCards = Array.from(this.carousel.querySelectorAll('.project-card'));
    this.totalProjects = this.projectCards.length;
    
    if (this.totalProjects === 0) return;

    this.dots = Array.from(document.querySelectorAll('.carousel-dot'));
    this.prevBtn = document.querySelector('.carousel-nav-prev');
    this.nextBtn = document.querySelector('.carousel-nav-next');

    // Touch/Swipe properties
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50; // minimum distance for swipe

    // Detect if mobile/tablet
    this.isMobile = window.innerWidth <= 1024;

    this.bindCarouselEvents();
    this.bindTouchEvents();
    this.bindLightboxEvents();
    this.updateCarouselPositions(false); // Initial setup without animation
    
    // Start auto-rotation only on desktop
    if (!this.isMobile && this.totalProjects > 1) {
      this.startAutoRotation();
    }
    
    // Update isMobile on window resize
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1024;
      
      // Start/stop auto-rotation based on screen size change
      if (wasMobile && !this.isMobile && this.totalProjects > 1) {
        this.startAutoRotation();
      } else if (!wasMobile && this.isMobile) {
        this.stopAutoRotation();
      }
    });
  }

  bindCarouselEvents() {
    // Pause auto-rotation on hover (desktop only)
    const carouselWrapper = document.querySelector('.projects-carousel-wrapper');
    if (carouselWrapper && !this.isMobile) {
      carouselWrapper.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.stopAutoRotation();
      });

      carouselWrapper.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (!this.isMobile && this.totalProjects > 1) {
          this.startAutoRotation();
        }
      });
    }

    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.navigate(-1));
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.navigate(1));
    }

    // Click on project cards
    this.projectCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        // Prevent click during animation
        if (this.isAnimating) return;
        
        const position = card.getAttribute('data-position');
        
        // Only open lightbox if this card is in center position
        if (position === 'center') {
          this.openLightbox(card);
        }
      });
    });

    // Carousel dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        if (this.isAnimating || index === this.currentIndex) return;
        this.currentIndex = index;
        this.updateCarouselPositions(true);
        
        // Restart auto-rotation timer after dot click (desktop only)
        if (!this.isMobile && !this.isHovered && this.totalProjects > 1) {
          this.startAutoRotation();
        }
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.lightbox.style.display === 'none' || !this.lightbox.style.display) {
        if (e.key === 'ArrowLeft') {
          this.navigate(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigate(1);
        }
      }
    });
  }

  bindTouchEvents() {
    const carouselContainer = document.querySelector('.projects-carousel-container');
    if (!carouselContainer) return;

    // Touch start
    carouselContainer.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    // Touch end
    carouselContainer.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.touchEndY = e.changedTouches[0].screenY;
      this.handleSwipe();
    }, { passive: true });
  }

  startAutoRotation() {
    // Clear any existing interval
    this.stopAutoRotation();
    
    // Start new interval
    this.autoRotateInterval = setInterval(() => {
      if (!this.isHovered && !this.isAnimating) {
        this.navigate(1); // Move to next
      }
    }, this.autoRotateDelay);
  }

  stopAutoRotation() {
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  }

  handleSwipe() {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = Math.abs(this.touchEndY - this.touchStartY);
    
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > this.minSwipeDistance) {
      if (deltaX > 0) {
        // Swiped right - go to previous
        this.navigate(-1);
      } else {
        // Swiped left - go to next
        this.navigate(1);
      }
    }
  }

  navigate(direction) {
    if (this.isAnimating) return;

    this.currentIndex += direction;
    
    // Loop around
    if (this.currentIndex < 0) {
      this.currentIndex = this.totalProjects - 1;
    } else if (this.currentIndex >= this.totalProjects) {
      this.currentIndex = 0;
    }

    this.updateCarouselPositions(true);
    
    // Restart auto-rotation timer after manual navigation (desktop only)
    if (!this.isMobile && !this.isHovered && this.totalProjects > 1) {
      this.startAutoRotation();
    }
  }

  updateCarouselPositions(animate = true) {
    if (animate) {
      this.isAnimating = true;
    }

    // FORCE Z-INDEX UPDATE IMMEDIATELY for all cards
    this.projectCards.forEach((card, index) => {
      // Calculate position relative to current index
      let position = index - this.currentIndex;
      
      // Normalize position for smooth wrap-around
      if (this.totalProjects > 2) {
        const half = Math.floor(this.totalProjects / 2);
        if (position < -half) {
          position += this.totalProjects;
        } else if (position > half) {
          position -= this.totalProjects;
        }
      }

      // Remove previous position
      card.removeAttribute('data-position');
      
      // Assign new position AND FORCE z-index with !important via style.cssText
      if (position === 0) {
        // CENTER - Always highest z-index
        card.setAttribute('data-position', 'center');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 100 !important;';
      } else if (position === -1) {
        // LEFT - Behind center
        card.setAttribute('data-position', 'left');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 10 !important;';
      } else if (position === 1) {
        // RIGHT - Behind center
        card.setAttribute('data-position', 'right');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 10 !important;';
      } else if (position === -2) {
        // FAR LEFT - Lower
        card.setAttribute('data-position', 'left-far');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 5 !important;';
      } else if (position === 2) {
        // FAR RIGHT - Lower
        card.setAttribute('data-position', 'right-far');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 5 !important;';
      } else if (position < -2) {
        // HIDDEN LEFT - Lowest
        card.setAttribute('data-position', 'hidden-left');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 1 !important;';
      } else if (position > 2) {
        // HIDDEN RIGHT - Lowest
        card.setAttribute('data-position', 'hidden-right');
        card.style.cssText = (card.style.cssText || '') + '; z-index: 1 !important;';
      }
    });

    // Update dots
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });

    // Re-enable after animation completes
    if (animate) {
      setTimeout(() => {
        this.isAnimating = false;
      }, 750); // Match CSS transition duration (0.7s + 50ms buffer)
    }
  }

  bindLightboxEvents() {
    const closeBtn = this.lightbox.querySelector('.lightbox-close');
    const overlay = this.lightbox.querySelector('.lightbox-overlay');
    const prevBtn = this.lightbox.querySelector('.lightbox-arrow-prev');
    const nextBtn = this.lightbox.querySelector('.lightbox-arrow-next');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeLightbox());
    }

    if (overlay) {
      overlay.addEventListener('click', () => this.closeLightbox());
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.navigateLightbox(-1));
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.navigateLightbox(1));
    }

    // Keyboard navigation for lightbox
    document.addEventListener('keydown', (e) => {
      if (this.lightbox.getAttribute('data-visible') === 'true') {
        if (e.key === 'Escape') {
          this.closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          this.navigateLightbox(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigateLightbox(1);
        }
      }
    });
  }

  openLightbox(card) {
    const galleryData = card.querySelector('.project-gallery-data');
    
    if (!galleryData) return;

    const images = Array.from(galleryData.querySelectorAll('img[data-gallery-src]'));
    
    if (images.length === 0) return;

    // Get project details from card data attributes
    this.currentProjectTitle = card.getAttribute('data-project-title') || '';
    this.currentProjectSubtitle = card.getAttribute('data-project-subtitle') || '';
    this.currentProjectDescription = card.getAttribute('data-project-description') || 'Featuring DUO Concepts\' signature space-saving furniture solutions designed specifically for modern urban living.';
    this.currentProjectDetails = card.getAttribute('data-project-details') || 'Every element carefully curated to maximize functionality while maintaining aesthetic excellence.';

    this.lightboxImages = images.map(img => ({
      src: img.getAttribute('data-gallery-src'),
      caption: img.getAttribute('data-gallery-caption') || ''
    }));

    this.currentLightboxIndex = 0;
    this.renderMagazineSpread();
    
    // Show lightbox with smooth animation
    this.lightbox.style.display = 'block';
    // Scroll to top
    const lightboxContent = this.lightbox.querySelector('.lightbox-content');
    if (lightboxContent) {
      lightboxContent.scrollTop = 0;
    }
    // Force reflow
    void this.lightbox.offsetWidth;
    this.lightbox.setAttribute('data-visible', 'true');
    document.body.style.overflow = 'hidden';
  }

  renderMagazineSpread() {
    const layout = this.lightbox.querySelector('#magazineLayout');
    if (!layout) return;

    const totalPages = this.lightboxImages.length;
    const currentPage = this.currentLightboxIndex;

    // Build magazine HTML
    let html = `
      <div class="magazine-header">
        <p class="magazine-issue">Project Portfolio — ${new Date().getFullYear()}</p>
        <h1 class="lightbox-project-title">${this.currentProjectTitle}</h1>
        <p class="lightbox-project-subtitle">${this.currentProjectSubtitle}</p>
      </div>
    `;

    // Render current page
    const currentImage = this.lightboxImages[currentPage];
    
    // Page layouts vary based on page number for visual interest
    if (currentPage === 0) {
      // First page: Hero image
      html += `
        <div class="magazine-page">
          <div class="magazine-hero">
            <img src="${currentImage.src}" alt="${currentImage.caption}">
            ${currentImage.caption ? `<p class="image-caption">${currentImage.caption}</p>` : ''}
          </div>
        </div>
      `;
    } else if (currentPage === totalPages - 1) {
      // Last page: Full width with text block
      html += `
        <div class="magazine-page">
          <div class="magazine-grid">
            <div class="full-width-image">
              <div class="magazine-image-block wide-image">
                <img src="${currentImage.src}" alt="${currentImage.caption}">
              </div>
              ${currentImage.caption ? `<p class="image-caption">${currentImage.caption}</p>` : ''}
            </div>
            <div class="magazine-text-block">
              <h3>About This Project</h3>
              <p>${this.currentProjectSubtitle}</p>
              <p>${this.currentProjectDescription}</p>
            </div>
          </div>
        </div>
      `;
    } else {
      // Middle pages: 2-column grid layout
      html += `
        <div class="magazine-page">
          <div class="magazine-grid">
            <div class="magazine-image-block tall-image">
              <img src="${currentImage.src}" alt="${currentImage.caption}">
            </div>
            ${currentImage.caption ? `
            <div class="magazine-text-block">
              <h3>Details</h3>
              <p>${currentImage.caption}</p>
              <p>${this.currentProjectDetails}</p>
            </div>
            ` : `
            <div class="magazine-image-block square-image">
              <img src="${this.lightboxImages[0].src}" alt="">
            </div>
            `}
          </div>
        </div>
      `;
    }

    layout.innerHTML = html;
    this.updateNavigationState();
  }

  updateNavigationState() {
    const counter = this.lightbox.querySelector('#lightboxCounter');
    const prevBtn = this.lightbox.querySelector('.lightbox-arrow-prev');
    const nextBtn = this.lightbox.querySelector('.lightbox-arrow-next');

    if (counter) {
      counter.textContent = `Page ${this.currentLightboxIndex + 1} / ${this.lightboxImages.length}`;
    }

    if (prevBtn) {
      prevBtn.disabled = this.currentLightboxIndex === 0;
    }

    if (nextBtn) {
      nextBtn.disabled = this.currentLightboxIndex === this.lightboxImages.length - 1;
    }
  }

  closeLightbox() {
    this.lightbox.setAttribute('data-visible', 'false');
    
    // Wait for animation before hiding
    setTimeout(() => {
      this.lightbox.style.display = 'none';
      document.body.style.overflow = '';
      this.lightboxImages = [];
      const layout = this.lightbox.querySelector('#magazineLayout');
      if (layout) {
        layout.innerHTML = '';
      }
    }, 500); // Match CSS transition duration
  }

  navigateLightbox(direction) {
    this.currentLightboxIndex += direction;

    if (this.currentLightboxIndex < 0) {
      this.currentLightboxIndex = 0;
    } else if (this.currentLightboxIndex >= this.lightboxImages.length) {
      this.currentLightboxIndex = this.lightboxImages.length - 1;
    }

    this.renderMagazineSpread();
    
    // Scroll to top smoothly
    const lightboxContent = this.lightbox.querySelector('.lightbox-content');
    if (lightboxContent) {
      lightboxContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TradeProjects3DCarousel();
  });
} else {
  new TradeProjects3DCarousel();
}
